"use client";

import * as React from "react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, isDefined } from "@/lib/utils";
import { functionalUpdate } from "@tanstack/react-table";
import { DateRange, OnSelectHandler } from "react-day-picker";
import { CalendarProps } from "./calendar";
import {
  getHours,
  getMinutes,
  setHours,
  setMinutes,
  getSeconds,
  getMilliseconds,
  setMilliseconds,
  setSeconds,
  endOfDay,
  isEqual,
  startOfDay,
} from "date-fns";
import usePrevious from "@/hooks/use-previous";

export type TimePickerProps = {
  selected?: Date;
  onSelect?: OnSelectHandler<Date | undefined>;
  intervals?: number[];
  withAmPm?: boolean;
} & Pick<CalendarProps, "timeZone"> &
  Omit<React.ComponentPropsWithoutRef<"div">, "onSelect" | "selected">;

const secondMs = 1000;
const minuteMs = 60 * 1000;
const hourMs = 60 * 60 * 1000;
const dayMs = 24 * 60 * 60 * 1000;

const timeSplit = (time: number) => {
  const milliseconds = Math.floor(time / 1) % 1000;
  const seconds = Math.floor(time / secondMs) % 60;
  const minutes = Math.floor(time / minuteMs) % 60;
  const hours = Math.floor(time / hourMs) % 24;
  return { hours, minutes, seconds, milliseconds };
};

const intervalsGetIndexes = (intervals: number[], time: number): number[] => {
  const result: number[] = [...Array(intervals.length)];
  for (let i = 0; i < intervals.length; i++) {
    const current = intervals[i];
    result[i] = Math.floor(time / current);
    time = time % current;
  }
  return result;
};

const intervalsGetTime = (intervals: number[], indexes: number[]): number => {
  let time = 0;
  for (let i = 0; i < indexes.length && i < intervals.length; i++) {
    time += intervals[i] * indexes[i];
  }
  return time;
};

const intervalsGetSteps = (intervals: number[]): number[] => {
  let prev = dayMs;
  const result: number[] = [...Array(intervals.length)];
  for (let i = 0; i < intervals.length; i++) {
    const current = intervals[i];
    result[i] = prev / current;
    prev = current;
  }
  // console.log({ steps: result });
  return result;
};

const getEnd = (indexes: number[]) => {
  for (let i = indexes.length - 1; i >= 0; i--) {
    const current = indexes[i];
    if (current > 0) {
      return i;
    }
  }
  return 0;
};

const defaultIntervals = [
  30 * 60 * 1000,
  // 10 * 60 * 1000,
  // 5 * 60 * 1000,
  // 60 * 1000,
];

const setTime = (date: Date, time: number) =>
  setHours(
    setMinutes(
      setSeconds(
        setMilliseconds(date, Math.floor(time / 1) % 1000),
        Math.floor(time / secondMs) % 60,
      ),
      Math.floor(time / minuteMs) % 60,
    ),
    Math.floor(time / hourMs) % 24,
  );

const useUpdate = <T,>({
  selected,
  onSelect,
}: {
  selected: T;
  onSelect?: OnSelectHandler<T>;
}) => {
  const selectedRef = React.useRef(selected);
  React.useEffect(() => {
    selectedRef.current = selected;
  }, [selectedRef, selected]);
  return React.useCallback(
    (
      a: React.SetStateAction<T>,
      ev: React.MouseEvent | React.KeyboardEvent | React.TouchEvent,
    ) => {
      const next = functionalUpdate(a, selectedRef.current);
      if (onSelect) {
        onSelect(
          next,
          undefined as any,
          {},
          ev as React.MouseEvent | React.KeyboardEvent,
        );
      }
      return next;
    },
    [onSelect],
  );
};

const useDateParts = (date: Date | undefined) => {
  const hour = date ? getHours(date) : undefined;
  const minute = date ? getMinutes(date) : undefined;
  const second = date ? getSeconds(date) : undefined;
  const millisecond = date ? getMilliseconds(date) : undefined;
  const time = date
    ? millisecond! +
      1000 * second! +
      60 * 1000 * minute! +
      60 * 60 * 1000 * hour!
    : undefined;
  const prevTime = usePrevious(time);
  const isStartOfDay = date ? isEqual(date, startOfDay(date)) : undefined;
  const isEndOfDay = date ? isEqual(date, endOfDay(date)) : undefined;
  return {
    hour,
    minute,
    second,
    millisecond,
    time,
    prevTime,
    isStartOfDay,
    isEndOfDay,
  };
};

export function TimePicker({
  selected,
  onSelect,
  className,
  timeZone,
  intervals = defaultIntervals,
  withAmPm = true,
  ...props
}: TimePickerProps) {
  const selectedParts = useDateParts(selected);
  const update = useUpdate({ selected, onSelect });
  const firstScrollRef = React.useRef(true);
  const Base = React.useCallback(
    ({
      indexes: _indexes,
      time: _time,
      onClick,
      depth,
    }: {
      indexes?: number[];
      time?: number;
      onClick?: React.MouseEventHandler<HTMLDivElement>;
      depth: number;
    }) => {
      const time = (_time ??
        (_indexes ? intervalsGetTime(intervals, _indexes) : undefined))!;
      const indexes = (_indexes ?? intervalsGetIndexes(intervals, time))!;
      const end = isDefined(indexes) ? getEnd(indexes) : undefined;
      const minimal = isDefined(end) ? intervals[end] : 1;
      const renderMinutes = minimal <= hourMs;
      const renderSeconds = withAmPm ? minimal < minuteMs : renderMinutes;
      const renderMilliseconds = minimal <= secondMs;
      const { hours, minutes, seconds, milliseconds } = timeSplit(time);
      const displayHours = withAmPm ? (hours === 12 ? 12 : hours % 12) : hours;
      const amPm = withAmPm ? (hours / 12 < 1 ? "AM" : "PM") : "";
      const key = isDefined(indexes) ? indexes.join("_") : time;
      return (
        <div
          key={key}
          ref={
            time === selectedParts.time
              ? (ref) => {
                  if (ref) {
                    const target =
                      (firstScrollRef.current
                        ? ref.nextElementSibling || ref.previousElementSibling
                        : undefined) || ref;
                    firstScrollRef.current = false;
                    target.scrollIntoView({
                      behavior: selectedParts.prevTime ? "smooth" : "instant",
                      block: "center",
                    });
                  }
                }
              : null
          }
          className={cn(
            "cursor-pointer bg-background rounded-md w-full",
            time === selectedParts.time && "sticky top-1 bottom-1",
          )}
        >
          <div
            className={cn(
              "rounded-md w-full py-2 px-2 text-center",
              time === selectedParts.time
                ? "border-2 border-azure bg-azure/20 hover:bg-azure/25"
                : "bg-muted/60 hover:bg-muted",
            )}
            // style={{ marginLeft: `${-10 + end * 10}px` }}
            onClick={(ev) => {
              update((s) => (s ? setTime(s, time) : s), ev);
              if (onClick) onClick(ev);
            }}
          >
            {`${displayHours.toString().padStart(2, "0")}${!renderMinutes ? "" : `:${minutes.toString().padStart(2, "0")}${!renderSeconds ? "" : `:${seconds.toString().padStart(2, "0")}${!renderMilliseconds ? "" : `.${milliseconds.toString().padStart(3, "0")}`}`}`} ${amPm}`}
          </div>
        </div>
      );
    },
    [update, intervals, selectedParts.time],
  );
  const steps = React.useMemo(() => intervalsGetSteps(intervals), [intervals]);
  // const maxDepthState = React.useState(0);
  const Step = React.useCallback(
    ({
      depth,
      indexes,
      carry,
    }: {
      depth: number;
      indexes: number[];
      carry: number;
    }) => {
      // const [maxDepth, setMaxDepth] = maxDepthState;
      const step = steps[depth];
      const items: any[] = [];
      const interval = intervals[depth];
      for (let i = 0; i < step; i++) {
        const key = `${depth}_${i}`;
        const next = carry + interval;
        const current = [...indexes, i];
        if (
          depth + 1 < steps.length &&
          isDefined(selectedParts.time) &&
          carry <= selectedParts.time &&
          selectedParts.time < next
        ) {
          items.push(
            <div
              key={key}
              className={cn(
                "flex flex-col items-center gap-1",
                // "p-1 border-2 border-muted",
              )}
            >
              <Step depth={depth + 1} indexes={current} carry={carry} />
              {/* {depth < maxDepth ? (
                <Step depth={depth + 1} indexes={current} carry={carry} />
              ) : (
                <div className="flex items-center">
                  <Base time={selectedParts.time} />
                  <BiChevronLeft
                    className="ml-auto"
                    onClick={(ev) => setMaxDepth(depth + 1)}
                  />
                </div>
              )} */}
            </div>,
          );
        } else {
          items.push(
            <React.Fragment key={key}>
              <Base
                indexes={current}
                depth={depth}
                // onClick={(ev) => setMaxDepth((d) => Math.max(d - 1, 0))}
              />
              {selectedParts.time &&
              carry < selectedParts.time &&
              selectedParts.time < next ? (
                <Base time={selectedParts.time} depth={depth} />
              ) : undefined}
            </React.Fragment>,
          );
        }
        carry = next;
      }

      return items;
    },
    [steps, intervals, Base, selectedParts.time],
  );

  return (
    <div {...props} className={cn("flex items-center", className)}>
      <ScrollArea
        className="h-full min-w-8 border-l border-muted shadow px-2 py-1"
        style={{ clipPath: "inset(0px 0px 0px -12px)" }}
        viewport={{
          className: "flex flex-col items-center gap-1 w-full h-full",
        }}
      >
        <Step depth={0} indexes={[]} carry={0} />
      </ScrollArea>
    </div>
  );
}

export type TimePickerRangeProps = {
  selected?: DateRange;
  onSelect?: OnSelectHandler<DateRange | undefined>;
  intervals?: number[];
  withAmPm?: boolean;
} & Pick<CalendarProps, "timeZone"> &
  Omit<React.ComponentPropsWithoutRef<"div">, "onSelect" | "selected">;

export function TimePickerRange({
  selected,
  onSelect,
  className,
  timeZone,
  intervals = defaultIntervals,
  withAmPm = true,
  ...props
}: TimePickerRangeProps) {
  const firstScrollRef = React.useRef(true);
  const fromParts = useDateParts(selected?.from);
  const toParts = useDateParts(selected?.to);
  const update = useUpdate({ selected, onSelect });
  const Base = React.useCallback(
    ({
      indexes: _indexes,
      time: _time,
      onClick,
    }: {
      indexes?: number[];
      time?: number;
      onClick?: React.MouseEventHandler<HTMLDivElement>;
    }) => {
      const time = (_time ??
        (_indexes ? intervalsGetTime(intervals, _indexes) : undefined))!;
      const indexes = (_indexes ?? intervalsGetIndexes(intervals, time))!;
      const end = isDefined(indexes) ? getEnd(indexes) : undefined;
      const minimal = isDefined(end) ? intervals[end] : 1;
      const renderMinutes = minimal <= hourMs;
      const renderSeconds = withAmPm ? minimal < minuteMs : renderMinutes;
      const renderMilliseconds = minimal <= secondMs;
      const { hours, minutes, seconds, milliseconds } = timeSplit(time);
      const displayHours = withAmPm ? (hours === 12 ? 12 : hours % 12) : hours;
      const amPm = withAmPm ? (hours / 12 < 1 ? "AM" : "PM") : "";
      const key = isDefined(indexes) ? indexes.join("_") : time;
      const left = time === fromParts.time;
      const right = time === toParts.time;
      const full = left && right;
      const partial = !full;

      return (
        <div
          className={cn(
            "cursor-pointer text-center rounded-md w-full flex bg-background",
            full
              ? "sticky top-1 bottom-1"
              : left
                ? "sticky top-1"
                : right
                  ? "sticky bottom-1"
                  : "",
            fromParts.time !== undefined && !left
              ? "top-11"
              : toParts.time !== undefined && !right
                ? "bottom-11"
                : undefined,
          )}
        >
          <div
            key={key}
            ref={
              time === fromParts.time || time === toParts.time
                ? (ref) => {
                    if (ref) {
                      const target =
                        (firstScrollRef.current
                          ? ref.parentElement?.previousElementSibling ||
                            ref.parentElement?.nextElementSibling
                          : undefined) || ref;
                      if (
                        firstScrollRef.current ||
                        (time === fromParts.time &&
                          (!fromParts.prevTime ||
                            fromParts.prevTime !== fromParts.time)) ||
                        (time === toParts.time &&
                          (!toParts.prevTime ||
                            toParts.prevTime !== toParts.time))
                      ) {
                        target.scrollIntoView({
                          behavior:
                            fromParts.prevTime || toParts.prevTime
                              ? "smooth"
                              : "instant",
                          block: "center",
                        });
                      }
                      firstScrollRef.current = false;
                    }
                  }
                : null
            }
            className={cn(
              "cursor-pointer text-center rounded-md w-full flex",
              full
                ? "bg-azure/20 hover:bg-azure/25"
                : left
                  ? "bg-azure/20 hover:bg-azure/25"
                  : right
                    ? "bg-azure/20 hover:bg-azure/25"
                    : "bg-muted/60 hover:bg-muted",
            )}
          >
            <div
              className={cn(
                "rounded-l w-2 h-full",
                left && !right
                  ? "border-l-2 border-t-2 border-b-2 border-azure"
                  : right && !left
                    ? "hover:bg-azure/10"
                    : partial && "bg-muted/60 hover:bg-border/60",
              )}
              onClick={(ev) => {
                if (full) return;
                update((s) => {
                  if (!s) return;
                  return {
                    ...s,
                    from: s?.from ? setTime(s.from, time) : s.from,
                  };
                }, ev);
                if (onClick) onClick(ev);
              }}
            ></div>
            <div
              className="py-2 w-full"
              onClick={(ev) => {
                update((s) => {
                  if (!s) return;
                  if (
                    !left &&
                    (right ||
                      fromParts.time === undefined ||
                      (toParts.time !== undefined && time < fromParts.time) ||
                      (toParts.time !== undefined &&
                        (fromParts.isStartOfDay || !toParts.isEndOfDay) &&
                        fromParts.time !== undefined &&
                        fromParts.time < time &&
                        time < toParts.time &&
                        Math.abs(time - fromParts.time) <
                          Math.abs(time - toParts.time)))
                  ) {
                    return {
                      from: s?.from ? setTime(s.from, time) : s.from,
                      to: s?.to,
                    };
                  } else {
                    return {
                      from: s?.from,
                      to: s?.to ? setTime(s.to, time) : s.to,
                    };
                  }
                }, ev);
                if (onClick) onClick(ev);
              }}
            >
              {`${displayHours.toString().padStart(2, "0")}${!renderMinutes ? "" : `:${minutes.toString().padStart(2, "0")}${!renderSeconds ? "" : `:${seconds.toString().padStart(2, "0")}${!renderMilliseconds ? "" : `.${milliseconds.toString().padStart(3, "0")}`}`}`} ${amPm}`}
            </div>
            <div
              className={cn(
                "rounded-r w-2 h-full",
                right && !left
                  ? "border-r-2 border-t-2 border-b-2 border-azure"
                  : left && !right
                    ? "hover:bg-azure/10"
                    : partial && "bg-muted/60 hover:bg-border/60",
              )}
              onClick={(ev) => {
                if (full) return;
                update((s) => {
                  if (!s) return;
                  return {
                    ...s,
                    to: s?.to ? setTime(s.to, time) : s.to,
                  };
                }, ev);
                if (onClick) onClick(ev);
              }}
            ></div>
          </div>
        </div>
      );
    },
    [update, intervals, fromParts.time, toParts.time],
  );
  const steps = React.useMemo(() => intervalsGetSteps(intervals), [intervals]);
  // const maxDepthState = React.useState(0);
  const Step = React.useCallback(
    ({
      depth,
      indexes,
      carry,
    }: {
      depth: number;
      indexes: number[];
      carry: number;
    }) => {
      // const [maxDepth, setMaxDepth] = maxDepthState;
      const step = steps[depth];
      const items: any[] = [];
      const interval = intervals[depth];
      for (let i = 0; i < step; i++) {
        const key = `${depth}_${i}`;
        const next = carry + interval;
        const current = [...indexes, i];
        if (
          depth + 1 < steps.length &&
          ((isDefined(fromParts.time) &&
            carry <= fromParts.time &&
            fromParts.time < next) ||
            (isDefined(toParts.time) &&
              carry <= toParts.time &&
              toParts.time < next))
        ) {
          items.push(
            <div
              key={key}
              className={cn(
                "flex flex-col items-center gap-1",
                // "p-1 border-2 border-muted",
              )}
            >
              <Step depth={depth + 1} indexes={current} carry={carry} />
              {/* {depth < maxDepth ? (
                <Step depth={depth + 1} indexes={current} carry={carry} />
              ) : (
                <div className="flex items-center">
                  <Base time={selectedParts.time} />
                  <BiChevronLeft
                    className="ml-auto"
                    onClick={(ev) => setMaxDepth(depth + 1)}
                  />
                </div>
              )} */}
            </div>,
          );
        } else {
          items.push(
            <React.Fragment key={key}>
              <Base
                indexes={current}
                // onClick={(ev) => setMaxDepth((d) => Math.max(d - 1, 0))}
              />
              {fromParts.time &&
              carry < fromParts.time &&
              fromParts.time < next ? (
                <Base time={fromParts.time} />
              ) : undefined}
              {toParts.time && carry < toParts.time && toParts.time < next ? (
                <Base time={toParts.time} />
              ) : undefined}
            </React.Fragment>,
          );
        }
        carry = next;
      }

      return items;
    },
    [steps, intervals, Base, fromParts.time, toParts.time],
  );

  return (
    <div {...props} className={cn("flex items-center", className)}>
      <ScrollArea
        className="h-full min-w-8 border-l border-muted shadow px-2"
        style={{ clipPath: "inset(0px 0px 0px -12px)" }}
      >
        <div className="flex flex-col items-center gap-1 w-full h-full py-1">
          <Step depth={0} indexes={[]} carry={0} />
        </div>
      </ScrollArea>
    </div>
  );
}
